import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Row,
  Col,
  Table,
  Pagination,
  Select,
  Button,
  Icon,
  Input,
  Tooltip,
  DatePicker
} from "antd";
import debounce from "lodash/debounce";
import { strings } from "../../helpers";
import UploadAdCampaignLeads from "./UploadAdCampaignLeads";

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

const LeadsView = ({
  leads,
  allRecords,
  pageNo,
  perPage,
  onPageChange,
  handleChangePageSizeChange,
  clinics,
  fetchClinicsList,
  handleSelectClinic,
  gotoCampaigns,
  filterParams,
  searchText,
  handleSearchText,
  handleSearch,
  handleExportLeads,
  onDateFilterSelect,
  loadMoreClinics,
  hasMoreClinics,
  clinicsSearch
}) => {
  const [uploadModalVisible, setUploadModalVisible] = React.useState(false);
  const handleOpenUploadModal = () => setUploadModalVisible(true);
  const handleCloseUploadModal = () => setUploadModalVisible(false);

  const columns = [
    {
      title: "Name",
      key: "name",
      width: 150,
      dataIndex: "campaign_name"
    },
    {
      title: "Clinic",
      key: "clinic_name",
      dataIndex: "clinic_name"
    },
    {
      title: "Patient name",
      key: "patient_name",
      render: record => <div>{`${record.first_name} ${record.last_name}`}</div>
    },
    {
      title: "Mobile number",
      key: "mobile_number",
      render: record => <div>{`${record.phone_number}`}</div>
    },
    {
      title: "Patient email",
      key: "clinic_number",
      width: 200,
      dataIndex: "email"
    },
    {
      title: "Date",
      key: "date",
      render: record => (
        <div>
          {record.campaign_start_time
            ? moment(record.campaign_start_time).format("DD MMM YYYY")
            : ""}{" "}
          -{" "}
          {record.campaign_end_time
            ? moment(record.campaign_end_time).format("DD MMM YYYY")
            : ""}
        </div>
      )
    },
    {
      title: "UTM Params",
      key: "utm_params",
      align: "left",
      render: record => {
        const utmDetails = record.utm_details || {};
        return (
          <div>
            {Object.entries(utmDetails).map(([key, value]) => {
              if (value) {
                return (
                  <div key={key}>
                    {key}: {value}
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      }
    }
  ];
  return (
    <div className="content-container">
      <div className="backgroundWhite">
        <Row type="flex" align="middle" gutter={16} className="mb-20">
          {filterParams?.campaignId && (
            <Col>
              <Button type="link" size="small" onClick={gotoCampaigns}>
                <Icon type="left" />
              </Button>
            </Col>
          )}
          <Col className="heading-domain pl-0 mb-0">Campaign leads</Col>
        </Row>
        <Row className="mb-10" gutter={16} align="bottom" type="flex">
          <Col span={5}>
            <label>{strings.dashboard.searchBy}</label>
            <Search
              value={searchText}
              onChange={handleSearchText}
              placeholder="Patient name, number, email"
              onSearch={handleSearch}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col span={5}>
            <label>Select clinic:</label>
            <Select
              className="full-width"
              showSearch
              filterOption={false}
              onSearch={debounce(fetchClinicsList, 800)}
              onChange={handleSelectClinic}
              allowClear
            >
              {clinics.map((clinic, index) => (
                <Option key={index} value={clinic.id}>
                  {clinic.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={5}>
            <label className="filter-by-tag">Filter by date range</label>
            <RangePicker
              format="DD/MM/YYYY"
              minDate={new Date()}
              onChange={onDateFilterSelect}
            />
          </Col>
          <Col>
            <Tooltip title="Upload leads">
              <Button
                size="small"
                icon="upload"
                onClick={handleOpenUploadModal}
                style={{ marginRight: 8 }}
              />
            </Tooltip>
            <Tooltip title="Export leads">
              <Button
                size="small"
                onClick={handleExportLeads}
                icon="download"
              />
            </Tooltip>
          </Col>
        </Row>

        <Table pagination={false} dataSource={leads} columns={columns} />

        <div className="pagination-wrapper">
          <div className="total-results-wrap">
            <span className="result-count">{allRecords}</span>
            {allRecords > 1 ? strings.results : strings.result}
          </div>
          {allRecords > 10 && (
            <Pagination
              total={allRecords}
              current={pageNo}
              onChange={onPageChange}
              pageSize={perPage}
              defaultCurrent={pageNo}
              showSizeChanger
              showQuickJumper
              onShowSizeChange={handleChangePageSizeChange}
              pageSizeOptions={[
                "10",
                "50",
                "100",
                "150",
                "200",
                "250",
                "300",
                "350",
                "400",
                "450",
                "500"
              ]}
            />
          )}
        </div>
      </div>
      {uploadModalVisible && (
        <UploadAdCampaignLeads
          visible={uploadModalVisible}
          onClose={handleCloseUploadModal}
          clinics={clinics}
          fetchClinicsList={fetchClinicsList}
          loadMoreClinics={loadMoreClinics}
          hasMoreClinics={hasMoreClinics}
          clinicsSearch={clinicsSearch}
        />
      )}
    </div>
  );
};

LeadsView.propTypes = {
  leads: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allRecords: PropTypes.number.isRequired,
  pageNo: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  handleChangePageSizeChange: PropTypes.func.isRequired,
  clinics: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchClinicsList: PropTypes.func.isRequired,
  handleSelectClinic: PropTypes.func.isRequired,
  gotoCampaigns: PropTypes.func.isRequired,
  filterParams: PropTypes.shape({
    campaignId: PropTypes.string
  }),
  searchText: PropTypes.string,
  handleSearchText: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleExportLeads: PropTypes.func.isRequired,
  onDateFilterSelect: PropTypes.func.isRequired,
  loadMoreClinics: PropTypes.func.isRequired,
  hasMoreClinics: PropTypes.bool.isRequired,
  clinicsSearch: PropTypes.string
};

LeadsView.defaultProps = {
  filterParams: {},
  searchText: "",
  clinicsSearch: ""
};

export default LeadsView;
